import { read, update, clearPrivate, pendingCount } from "./storage.mjs";
export async function api(path, body, userId) {
  const r = await fetch(path, {
    credentials: "same-origin",
    cache: "no-store",
    ...(body === undefined
      ? {}
      : {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-DM-Request": "1",
            ...(userId ? { "X-DM-User": userId } : {}),
          },
          body: JSON.stringify(body),
        }),
  });
  const data = await r.json();
  if (!r.ok)
    throw Object.assign(new Error(data.error || "Request failed"), {
      status: r.status,
      code: data.code,
    });
  return data;
}
export function createSync(onChange) {
  let generation = 0,
    flight = null;
  const notify = (status, local) => onChange({ status, local });
  // This non-private epoch changes only after account storage commits.
  // Read it as well as listening: storage events can be delivered late.
  const accountEpoch = () => localStorage.getItem("dm-account-epoch");
  const publish = () =>
    localStorage.setItem("dm-account-epoch", crypto.randomUUID());
  window.addEventListener("storage", async (event) => {
    if (!["dm-account-epoch", "dm-signed-out"].includes(event.key)) return;
    const epoch = ++generation,
      sharedEpoch = accountEpoch();
    const intent = localStorage.getItem("dm-signed-out");
    flight = null;
    const current = intent ? null : await read();
    if (
      epoch !== generation ||
      sharedEpoch !== accountEpoch() ||
      intent !== localStorage.getItem("dm-signed-out")
    )
      return;
    notify(current ? "Account updated in another tab" : "Signed out", current);
  });
  async function signout(confirmDiscard = () => false) {
    generation++;
    // Invalidate new writes before the deletion transaction. "guarded" is
    // recoverable after an interrupted request without discarding pending work.
    localStorage.setItem("dm-signed-out", "guarded");
    try {
      await clearPrivate(false);
    } catch (error) {
      localStorage.removeItem("dm-signed-out");
      publish();
      if (error.code !== "UNSYNCED") throw error;
      if (!confirmDiscard()) {
        notify("Signout cancelled. Your unsynced work is kept.", await read());
        return false;
      }
      localStorage.setItem("dm-signed-out", "1");
      await clearPrivate(true);
    }
    localStorage.setItem("dm-signed-out", "1");
    publish();
    notify("Signed out", null);
    if (navigator.onLine)
      try {
        await api("/api/logout", {});
      } catch {}
    return true;
  }
  async function login(username, password) {
    return signIn("/api/login", { username, password });
  }
  // Email sign-in learns which account it opened only from the server's answer, so switching
  // accounts with unsynced work on this device is confirmed afterwards instead of before.
  async function signIn(path, body, confirmDiscard = () => true) {
    const epoch = ++generation,
      sharedEpoch = accountEpoch();
    const intent = localStorage.getItem("dm-signed-out");
    const valid = () =>
      epoch === generation &&
      sharedEpoch === accountEpoch() &&
      intent === localStorage.getItem("dm-signed-out");
    const state = await api(path, body);
    if (!valid()) return null;
    const previous = await read();
    if (previous && previous.snapshot.user.id !== state.user.id && pendingCount(previous) && !confirmDiscard()) {
      notify("Your unsynced work is kept on this device. Sign in to that account again to sync it.", previous);
      return null;
    }
    const local = await update(
      (current) =>
        current?.snapshot.user.id === state.user.id
          ? { ...current, snapshot: state }
          : { snapshot: state, pending: [], additions: [], settings: null, pendingChallengeAttempts: [], cachedChallenges: [], challengePractice: [] },
      { guard: valid, signedOut: false },
    );
    // Completion delivery can be delayed after the actual transaction commits.
    // Never clear a newer signout intent or publish that stale snapshot.
    if (!valid() || !local) return null;
    localStorage.removeItem("dm-signed-out");
    publish();
    notify("Connected", local);
    return local;
  }
  async function sync() {
    if (flight) return flight;
    const epoch = generation,
      sharedEpoch = accountEpoch();
    const valid = () => epoch === generation && sharedEpoch === accountEpoch();
    const running = (async () => {
      let current = await read();
      if (!current || (await read("signedOut")) || !valid()) return;
      if (!navigator.onLine) {
        notify("Offline · changes saved on this device", current);
        return;
      }
      const userId = current.snapshot.user.id;
      do {
        const batch = {
          events: current.pending.slice(0, 500),
          additions: [...current.additions],
          ...(current.settings ? { settings: current.settings } : {}),
        };
        try {
          notify("Syncing…", current);
          const response = await api("/api/sync", batch, userId);
          if (!valid()) return;
          if (response.user.id !== userId)
            throw Object.assign(
              new Error("Sign in again to sync this account"),
              { status: 401 },
            );
          current = await update((latest) => {
            if (!valid() || latest?.snapshot.user.id !== userId) return latest;
            const accepted = new Set(response.acceptedIds);
            return {
              ...latest,
              snapshot: response,
              pending: latest.pending.filter((e) => !accepted.has(e.id)),
              additions: latest.additions.filter(
                (id) => !batch.additions.includes(id),
              ),
              settings:
                JSON.stringify(latest.settings) ===
                JSON.stringify(batch.settings || null)
                  ? null
                  : latest.settings,
            };
          });
          if (!valid()) return;
          // Challenge work has its own ledger. A closed round can reject a
          // queued attempt; keep its practice locally without class credit.
          const challengeBatch = current.pendingChallengeAttempts?.slice(0, 500) || [];
          if (challengeBatch.length) {
            const challengeResult = await api("/api/challenges/attempts", {attempts:challengeBatch}, userId);
            if (!valid()) return;
            current = await update((latest) => {
              if (!valid() || latest?.snapshot.user.id !== userId) return latest;
              const accepted = new Set(challengeResult.acceptedIds || []);
              const rejected = new Set(challengeResult.rejectedIds || []);
              return {...latest,
                pendingChallengeAttempts:(latest.pendingChallengeAttempts || []).filter(attempt => !accepted.has(attempt.id) && !rejected.has(attempt.id)),
                challengePractice:[...(latest.challengePractice || []), ...challengeBatch.filter(attempt => rejected.has(attempt.id))],
                cachedChallenges:challengeResult.challenges || latest.cachedChallenges || [],
              };
            });
          }
          const challengeState = await api("/api/challenges", undefined, userId);
          if (!valid()) return;
          current = await update((latest) =>
            valid() && latest?.snapshot.user.id === userId
              ? {...latest, cachedChallenges:challengeState.challenges || []}
              : latest,
          );
          if (!valid()) return;
          notify(
            response.conflictIds.length
              ? "Overlapping reviews recorded as practice. Your schedule follows the accepted review."
              : "All changes synced",
            current,
          );
        } catch (error) {
          const latest = await read();
          if (valid())
            notify(
              error.code === "ACCOUNT_CHANGED"
                ? "Account changed. Sign in again before syncing this device."
                : error.status === 401
                  ? "Sign in again to sync. Your unsynced work is safe on this device."
                  : `Sync paused: ${error.message}. Your work is saved on this device.`,
              latest,
            );
          return;
        }
      } while (pendingCount(current) && valid());
    })().finally(() => {
      if (flight === running) flight = null;
    });
    flight = running;
    return running;
  }
  // Restore is also a network race: a late /state response must obey the
  // same cross-tab epoch as an acknowledgement, including on first load.
  async function restore() {
    const epoch = generation,
      sharedEpoch = accountEpoch();
    const valid = () => epoch === generation && sharedEpoch === accountEpoch();
    if (localStorage.getItem("dm-signed-out") || (await read("signedOut"))) {
      if (!valid()) return null;
      try {
        await clearPrivate(localStorage.getItem("dm-signed-out") !== "guarded");
      } catch (error) {
        if (error.code !== "UNSYNCED") throw error;
        localStorage.removeItem("dm-signed-out");
        publish();
        const kept = await read();
        notify(
          "Interrupted signout cancelled. Your unsynced work is kept.",
          kept,
        );
        return kept;
      }
      localStorage.setItem("dm-signed-out", "1");
      if (navigator.onLine)
        try {
          await api("/api/logout", {});
        } catch {}
      return null;
    }
    const local = await read();
    if (!valid()) return null;
    if (local) {
      if (local.snapshot.user.role === "teacher" && !navigator.onLine)
        return null;
      notify("Saved on this device", local);
      return local;
    }
    if (navigator.onLine)
      try {
        const state = await api("/api/state");
        if (!valid() || localStorage.getItem("dm-signed-out")) return null;
        const restored = await update((current) =>
          valid() && !localStorage.getItem("dm-signed-out")
            ? current || {
                snapshot: state,
                pending: [],
                additions: [],
                settings: null,
                pendingChallengeAttempts: [],
                cachedChallenges: [],
                challengePractice: [],
              }
            : current,
        );
        if (valid()) notify("Connected", restored);
        return restored;
      } catch {}
    return null;
  }

  return { login, signIn, signout, sync, restore };
}
